import React from 'react';
import { figure1 } from '../assets';

const questions = [
  {
    n: '01',
    q: 'How does safety alignment impact model behavior?',
    a: (
      <>
        Through <strong>SSAH</strong>, we posit that safety alignment fundamentally
        alters a model's decision-making process by teaching an otherwise unsafe
        model to follow the correct reasoning pathways. The task reduces to a
        specialized binary classification &mdash; <em>fulfill</em> or{' '}
        <em>refuse</em> &mdash; based on safety considerations.
      </>
    ),
  },
  {
    n: '02',
    q: 'Why is safety alignment brittle, and why does it introduce an alignment tax?',
    a: (
      <>
        We assign attributes to individual computing units (input channels and
        output neurons). Findings show desired attributes can be achieved by{' '}
        <strong>repurposing units originally responsible for other functions</strong>{' '}
        &mdash; explaining both the brittleness of safety mechanisms and the
        alignment tax.
      </>
    ),
  },
  {
    n: '03',
    q: 'Can these issues of safety alignment be mitigated?',
    a: (
      <>
        By <strong>freezing safety-critical components</strong> during
        fine-tuning and <strong>repurposing redundant units</strong>, we
        mitigate brittleness and minimize alignment tax. The atomic functional
        unit for safety resides at the <em>neuron level</em>.
      </>
    ),
  },
];

function MotivationSection() {
  return (
    <section className="py-20 bg-ink-50/60 border-t border-ink-100">
      <div className="max-w-6xl mx-auto px-6">
        <p className="section-eyebrow mb-3">02 &middot; The Big Picture</p>
        <h2 className="section-title">Motivation</h2>

        <div className="mt-10 grid md:grid-cols-5 gap-10 items-start">
          <div className="md:col-span-3 prose-paper">
            <p>
              This study follows a structured three-step approach to critical
              questions of safety alignment in LLMs. First, we propose a
              hypothesis advancing the theoretical understanding of safety
              alignment. Second, we investigate two fundamental challenges
              within that framework. Finally, we propose targeted mitigation
              strategies for the identified issues.
            </p>

            <div className="mt-8 space-y-5">
              {questions.map((item) => (
                <div
                  key={item.n}
                  className="card p-5 md:p-6 hover:shadow-card transition-shadow"
                >
                  <div className="flex items-start gap-4">
                    <div className="shrink-0 h-9 w-9 rounded-lg bg-grad-brand-accent text-white grid place-items-center text-xs font-bold shadow-sm">
                      Q{item.n.replace(/^0/, '')}
                    </div>
                    <div>
                      <h3 className="text-base md:text-lg font-semibold text-ink-900 italic font-serif">
                        {item.q}
                      </h3>
                      <p className="mt-2 text-sm md:text-[0.95rem] text-ink-600 leading-relaxed">
                        {item.a}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="md:col-span-2 md:sticky md:top-24">
            <figure className="card p-3">
              <img
                src={figure1}
                alt="Overview of SSAH"
                className="w-full h-auto rounded-xl"
              />
              <figcaption className="figure-caption">
                Figure 1. Roadmap of the SSAH framework &mdash; from hypothesis
                to attribute-based analysis to mitigation.
              </figcaption>
            </figure>
          </div>
        </div>
      </div>
    </section>
  );
}

export default MotivationSection;
